/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { Module, Node, Waiting } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import {
    ICheckoutViewProps,
    ILineItem,
    ILineItemDeliveryGroup,
    ILineItems,
    IOrderSummary,
    IPickUpAtStore,
    IEmailDelivery,
    IInvoicePaymentSummary
} from '@msdyn365-commerce-modules/checkout';

import 'react-dates/initialize';
import 'react-dates/lib/css/_datepicker.css';
import { useState, useCallback, useEffect } from 'react';
import { DateRangePicker, FocusedInputShape } from 'react-dates';
import moment, { Moment } from 'moment';
import 'moment/locale/fr-ca';
import { ICheckoutProps } from '../definition-extensions/checkout.ext.props.autogenerated';


export const PickUpAtStoreComponent: React.FC<IPickUpAtStore> = ({ PickUpAtStore, label, location }) => (
    <Node {...PickUpAtStore}>
        {label}
        {location}
    </Node>
);

export const EmailDeliveryComponent: React.FC<IEmailDelivery> = ({ EmailDelivery, label }) => <Node {...EmailDelivery}>{label}</Node>;

export const LineItemComponent: React.FC<ILineItem> = ({ LineItem, item, pickUpAtStore, emailDelivery }) => (
    <Node {...LineItem}>
        {item}
        {pickUpAtStore && <PickUpAtStoreComponent {...pickUpAtStore} />}
        {emailDelivery && <EmailDeliveryComponent {...emailDelivery} />}
    </Node>
);

export const LineItemGroupComponent: React.FC<ILineItemDeliveryGroup> = ({ LineItemDeliveryGroup, LineItemList, heading, lineItems }) => (
    <Node {...LineItemDeliveryGroup}>
        {heading}
        <Node {...LineItemList}>
            {lineItems.map(lineItem => (
                <LineItemComponent key={lineItem.LineId} {...lineItem} />
            ))}
        </Node>
    </Node>
);

export const LineItemGroupComponentWithMultiplePickUp: React.FC<ILineItemDeliveryGroup> = ({
    LineItemDeliveryGroup,
    LineItemList,
    heading,
    lineItems,
    lineItemWraper,
    lineItemWraperIcon
}) => (
    <Node {...LineItemDeliveryGroup}>
        {lineItemWraperIcon}
        {lineItemWraper}
        {heading}
        <Node {...LineItemList}>
            {lineItems.map(lineItem => (
                <LineItemComponentWithMultiplePickUp key={lineItem.LineId} {...lineItem} />
            ))}
        </Node>
    </Node>
);

export const LineItemComponentWithMultiplePickUp: React.FC<ILineItem> = ({ LineItem, item, pickUpAtStore, emailDelivery }) => (
    <Node {...LineItem}>
        {item}
        {emailDelivery && <EmailDeliveryComponent {...emailDelivery} />}
    </Node>
);

export const PickUpAtStoreComponentWithMultiplePickUp: React.FC<IPickUpAtStore> = ({ PickUpAtStore, label, location }) => (
    <Node {...PickUpAtStore}>
        {label}
        {location}
    </Node>
);

export const LineItemsComponent: React.FC<ILineItems> = ({
    LineItems,
    Header,
    heading,
    editLink,
    itemsForPickup,
    itemsForShip,
    itemsForEmail,
    itemsGroupWithMulitplePickupMode
}) => (
    <Node {...LineItems}>
        <Node {...Header}>
            {heading}
            {editLink}
        </Node>
        {itemsGroupWithMulitplePickupMode === undefined && itemsForPickup && <LineItemGroupComponent {...itemsForPickup} />}
        {itemsGroupWithMulitplePickupMode === undefined && itemsForEmail && <LineItemGroupComponent {...itemsForEmail} />}
        {itemsGroupWithMulitplePickupMode === undefined && itemsForShip && <LineItemGroupComponent {...itemsForShip} />}
        {itemsGroupWithMulitplePickupMode !== undefined
            ? itemsGroupWithMulitplePickupMode.map((item, index) => {
                  return <LineItemGroupComponentWithMultiplePickUp {...item} key={index} />;
              })
            : null}
    </Node>
);



const OrderSummaryComponent: React.FC<IOrderSummary> = ({ heading, lines }) => (
    <div className='msc-order-summary-wrapper'>
        {heading}
        <div className='msc-order-summary__items'>
            {lines && (
                <>
                    {lines.subtotal}
                    {lines.shipping}
                    {lines.otherCharge}
                    {lines.tax}
                    {lines.totalDiscounts}
                    {lines.loyalty}
                    {lines.customerAccount}
                    {lines.giftCard}
                    {lines.orderTotal}
                </>
            )}
        </div>
    </div>
);

const PaymentSummaryComponent: React.FC<IInvoicePaymentSummary> = ({ heading, lines }) => (
    <div className='msc-invoice-summary-wrapper'>
        {heading}
        <div className='msc-invoice-summary__items'>
            {lines && (
                <>
                    {lines.invoices}
                    {lines.giftCard}
                    {lines.loyalty}
                    {lines.orderTotal}
                </>
            )}
        </div>
    </div>
);

const CheckoutView: React.FC<ICheckoutViewProps & ICheckoutProps<{}>> = props => {
    const {
        isPaymentVerificationRedirection,
        shouldEnableSinglePaymentAuthorizationCheckout,
        canShow,
        checkoutProps,
        headerProps,
        hasSalesOrder,
        hasInvoiceLine,
        bodyProps,
        mainProps,
        mainControlProps,
        sideProps,
        sideControlFirstProps,
        sideControlSecondProps,
        // termsAndConditionsProps,
        orderConfirmation,
        loading,
        alert,
        title,
        guidedForm,
        orderSummary,
        invoicePaymentSummary,
        lineItems,
        placeOrderButton,
        // termsAndConditions,
        keepShoppingButton,
        checkoutExpressPaymentContainer,
        checkoutErrorRef
    } = props;

    const DatePickerComponent = ({ language }: { language: string }) => {
        const siteLanguage = props.context.request.locale || "en"; 
        const isFrench = siteLanguage.toLowerCase() === "fr-ca";
        const currentLanguage = isFrench ? "fr-ca" : "en-us";
    
        const ExtensionPropertiesData =
            props.data.checkout.result?.checkoutCart.extensionProperties || [];
    
        const [startDate, setStartDate] = useState<Moment | null>(null);
        const [endDate, setEndDate] = useState<Moment | null>(null);
        const [focusedInput, setFocusedInput] = useState<FocusedInputShape | null>(null);
    
        // Ensure Moment locale updates when the language changes
        useEffect(() => {
            moment.locale(currentLanguage);
        }, [currentLanguage]);
    
        // Function to check if a property exists in extension properties
        const doesPropertyExist = useCallback(
            (key: string) => {
                return ExtensionPropertiesData.some((prop) => prop.Key === key);
            },
            [ExtensionPropertiesData]
        );
    
        // Ensure required properties exist in extension properties
        const initializeExtensionProperties = useCallback(() => {
            const rentalFromDateExists = doesPropertyExist("RENTALFROMDATE");
            const rentalToDateExists = doesPropertyExist("RENTALTODATE");
    
            if (rentalFromDateExists && rentalToDateExists) {
                return;
            }
    
            const updatedExtensionProperties = [...ExtensionPropertiesData];
    
            const updateOrAddProperty = (key: string, value?: string) => {
                const existingIndex = updatedExtensionProperties.findIndex(
                    (prop) => prop.Key === key
                );
    
                if (existingIndex !== -1) {
                    updatedExtensionProperties[existingIndex].Value = { StringValue: value };
                } else {
                    updatedExtensionProperties.push({ Key: key, Value: { StringValue: value } });
                }
            };
    
            if (!rentalFromDateExists) {
                updateOrAddProperty("RENTALFROMDATE", "");
            }
            if (!rentalToDateExists) {
                updateOrAddProperty("RENTALTODATE", "");
            }
    
            const checkoutCart = props.data.checkout.result?.checkoutCart;
            if (checkoutCart && checkoutCart.updateExtensionProperties) {
                try {
                    checkoutCart.updateExtensionProperties({
                        newExtensionProperties: updatedExtensionProperties,
                    });
                } catch (error) {
                    console.error("Error initializing extension properties:", error);
                }
            }
        }, [ExtensionPropertiesData, doesPropertyExist, props.data.checkout.result?.checkoutCart]);
    
        useEffect(() => {
            initializeExtensionProperties();
    
            const rentalFromDate = ExtensionPropertiesData.find(
                (prop) => prop.Key === "RENTALFROMDATE"
            )?.Value?.StringValue;
            const rentalToDate = ExtensionPropertiesData.find(
                (prop) => prop.Key === "RENTALTODATE"
            )?.Value?.StringValue;
    
            // Parse dates safely and check validity
            const parsedStartDate = rentalFromDate ? moment(rentalFromDate, moment.ISO_8601, true) : null;
            const parsedEndDate = rentalToDate ? moment(rentalToDate, moment.ISO_8601, true) : null;
    
            setStartDate(parsedStartDate?.isValid() ? parsedStartDate : null);
            setEndDate(parsedEndDate?.isValid() ? parsedEndDate : null);
        }, [initializeExtensionProperties, ExtensionPropertiesData]);
    
        const handleDatesChange = useCallback(
            ({ startDate, endDate }: { startDate: Moment | null; endDate: Moment | null }) => {
                if (startDate && !moment.isMoment(startDate)) {
                    console.error("Invalid startDate:", startDate);
                    return;
                }
                if (endDate && !moment.isMoment(endDate)) {
                    console.error("Invalid endDate:", endDate);
                    return;
                }
    
                setStartDate(startDate);
                setEndDate(endDate);
    
                const updatedExtensionProperties = [...ExtensionPropertiesData];
    
                const updateOrAddProperty = (key: string, value?: string) => {
                    const existingIndex = updatedExtensionProperties.findIndex(
                        (prop) => prop.Key === key
                    );
    
                    if (existingIndex !== -1) {
                        updatedExtensionProperties[existingIndex].Value = { StringValue: value };
                    } else {
                        updatedExtensionProperties.push({ Key: key, Value: { StringValue: value } });
                    }
                };
    
                const formattedStartDate = startDate ? startDate.format("YYYY-MM-DD") : undefined;
                const formattedEndDate = endDate ? endDate.format("YYYY-MM-DD") : undefined;
    
                updateOrAddProperty("RENTALFROMDATE", formattedStartDate);
                updateOrAddProperty("RENTALTODATE", formattedEndDate);
    
                const checkoutCart = props.data.checkout.result?.checkoutCart;
                if (checkoutCart && checkoutCart.updateExtensionProperties) {
                    try {
                        checkoutCart.updateExtensionProperties({
                            newExtensionProperties: updatedExtensionProperties,
                        });
                    } catch (error) {
                        console.error("Error updating extension properties:", error);
                    }
                }
            },
            [ExtensionPropertiesData, props.data.checkout.result?.checkoutCart]
        );
    
        const handleFocusChange = useCallback((focusedInput: FocusedInputShape | null) => {
            setFocusedInput(focusedInput);
        }, []);
    
        return (
            <div className="datepicker_wrapper">
                <h4>{props.resources.rentalPeriodText}</h4>
                <DateRangePicker
                    startDate={startDate}
                    startDateId="start_date_id"
                    endDate={endDate}
                    endDateId="end_date_id"
                    onDatesChange={handleDatesChange}
                    focusedInput={focusedInput}
                    onFocusChange={handleFocusChange}
                    displayFormat="YYYY-MM-DD"
                    isOutsideRange={() => false}
                    showClearDates={true}
                    numberOfMonths={2}
                />
            </div>
        );
    };
    const cartLines = props.data?.checkout?.result?.checkoutCart?.cart?.CartLines || [];
    const hasQuoteRequestItem = cartLines.some(line => {
        const extProps = line.ExtensionProperties || [];

        const serialProp = extProps.find(p => p.Key === "SerialNo");
        const buyNowProp = extProps.find(p => p.Key === "BuyNow");

        const serialNo = serialProp?.Value?.StringValue || "";
        const buyNow = buyNowProp?.Value?.StringValue || "0";

        return !serialNo && buyNow === "0";
    });

    const shouldShowOrderSummary = cartLines.some(line => {
        const extProps = line.ExtensionProperties || [];

        const serialProp = extProps.find(p => p.Key === "SerialNo");
        const buyNowProp = extProps.find(p => p.Key === "BuyNow");

        const serialNo = serialProp?.Value?.StringValue || "";
        const buyNow = buyNowProp?.Value?.StringValue || "0";

        return serialNo && buyNow === "1";
    });


    const hasUsedBuyNow = cartLines.some((line, index) => {
        const extProps = line.ExtensionProperties || [];
        const serialProp = extProps.find(p => p.Key === "SerialNo");
        const buyNowProp = extProps.find(p => p.Key === "BuyNow");
        const serialNo = serialProp?.Value?.StringValue?.trim() || "";
        const buyNow = buyNowProp?.Value?.StringValue?.trim() || "";
        const isUsedBuyNow = serialNo !== "" && buyNow === "1";
        return isUsedBuyNow;
    });

    useEffect(() => {
    if (hasUsedBuyNow) return;
    
    const interval = setInterval(() => {
        const cards = document.querySelectorAll('.ms-checkout__guided-card');
    
        if (cards.length > 2) {
        const thirdCard = cards[2] as HTMLElement;
    
        if (thirdCard.classList.contains('expanded')) {
            const button = thirdCard.querySelector(
            '.ms-checkout__guided-card-body .ms-checkout__guided-card-footer button'
            ) as HTMLButtonElement | null;
    
            if (button && !button.classList.contains('auto-buy-now-triggered')) {
            button.classList.add('auto-buy-now-triggered');
    
            thirdCard.style.display = 'none';
            console.log('Third card is expanded. Clicking button...');
            setTimeout(() => {
                button.click();
            }, 2000);
    
            clearInterval(interval); // Stop polling
            }
        }
        }
    }, 300);
    
    return () => clearInterval(interval);
    }, [hasUsedBuyNow]);

    const DeliveryOptions = () => {
        const [isDelivery, setIsDelivery] = useState<boolean>(true);
        const checkoutCart = props.data.checkout.result?.checkoutCart;
        const ExtensionPropertiesData =
            props.data.checkout.result?.checkoutCart.extensionProperties || [];
    
        const updateOrAddProperty = useCallback(
            (key: string, value: string) => {
                const updatedExtensionProperties = [...ExtensionPropertiesData];
                const existingIndex = updatedExtensionProperties.findIndex((prop) => prop.Key === key);
    
                if (existingIndex !== -1) {
                    // Update the existing key
                    updatedExtensionProperties[existingIndex].Value = { StringValue: value };
                } else {
                    // Add the new key
                    updatedExtensionProperties.push({ Key: key, Value: { StringValue: value } });
                }
    
                if (checkoutCart?.updateExtensionProperties) {
                    try {
                        checkoutCart.updateExtensionProperties({
                            newExtensionProperties: updatedExtensionProperties,
                        });
                    } catch (error) {
                    }
                }
            },
            [ExtensionPropertiesData, checkoutCart]
        );
    
        useEffect(() => {
            const deliveryOptionProperty = ExtensionPropertiesData.find(
                (prop) => prop.Key === 'DELIVERYOPTION'
            );
    
            if (deliveryOptionProperty) {
                const deliveryOptionValue = deliveryOptionProperty.Value?.StringValue;
                setIsDelivery(deliveryOptionValue === 'Yes');
            } else {
                // Default to "Yes" if the key is not present
                updateOrAddProperty('DELIVERYOPTION', 'Yes');
                setIsDelivery(true);
            }
        }, [ExtensionPropertiesData, updateOrAddProperty]);
    
        const handleChange = useCallback(
            (event: React.ChangeEvent<HTMLInputElement>) => {
                const value = event.target.value === 'yes';
                setIsDelivery(value);
    
                const stringValue = value ? 'Yes' : 'No';
                updateOrAddProperty('DELIVERYOPTION', stringValue);
            },
            [updateOrAddProperty]
        );
    
        return (
            <div className="delivery_wrapper">
                <h4>{props.resources.deliveryRequiredText}</h4>
                <div className="radio-group">
                    <label>
                        <input
                            type="radio"
                            name="deliveryOption"
                            value="yes"
                            checked={isDelivery}
                            onChange={handleChange}
                        />
                        {props.resources.yesText}
                    </label>
                    <label>
                        <input
                            type="radio"
                            name="deliveryOption"
                            value="no"
                            checked={!isDelivery}
                            onChange={handleChange}
                        />
                        {props.resources.noText}
                    </label>
                </div>
            </div>
        );
    };    

    return (
        <Module {...checkoutProps} ref={checkoutErrorRef}>
            {!hasSalesOrder && !checkoutExpressPaymentContainer && <Node {...headerProps}>{title}</Node>}
            {!hasSalesOrder && isPaymentVerificationRedirection && shouldEnableSinglePaymentAuthorizationCheckout && !alert && !loading && (
                <Waiting className='msc-waiting-circular msc-waiting-lg' />
            )}
            {!hasSalesOrder && (
                <Node {...bodyProps}>
                    {loading}
                    {alert}
                    {canShow && (
                        <>
                            <Node {...mainProps}>
                                {checkoutExpressPaymentContainer}
                                {checkoutExpressPaymentContainer && <Node {...headerProps}>{title}</Node>}
                                {guidedForm}
                                {/* <Node {...termsAndConditionsProps}>{termsAndConditions}</Node> */}
                                <Node {...mainControlProps}>
                                    {placeOrderButton}
                                    {keepShoppingButton}
                                </Node>
                            </Node>
                            <Node {...sideProps}>
                                {hasQuoteRequestItem && <DatePickerComponent language="en" />}
                                {hasQuoteRequestItem && <DeliveryOptions />}
                                {!hasInvoiceLine ? (
                                    shouldShowOrderSummary && orderSummary && (
                                        <OrderSummaryComponent {...orderSummary} />
                                    )
                                ) : (
                                    invoicePaymentSummary && <PaymentSummaryComponent {...invoicePaymentSummary} />
                                )}
                                <Node {...sideControlFirstProps}>
                                    {/* <Node {...termsAndConditionsProps}>{termsAndConditions}</Node> */}
                                    {placeOrderButton}
                                    {keepShoppingButton}
                                </Node>
                                {lineItems && <LineItemsComponent {...lineItems} />}
                                <Node {...sideControlSecondProps}>
                                    {/* <Node {...termsAndConditionsProps}>{termsAndConditions}</Node> */}
                                    {placeOrderButton}
                                    {keepShoppingButton}
                                </Node>
                            </Node>
                        </>
                    )}
                </Node>
            )}
            {hasSalesOrder && orderConfirmation}
        </Module>
    );
};

export default CheckoutView;
